import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

import { CwBanner } from '@bbraun/cortex-angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { filter, startWith, Subject } from 'rxjs';

import { ViewAccessReason } from '../../../../definitions/enums/switch-reasons/view-access.reason';
import { Country } from '../../../../definitions/interfaces/country/country';
import { BaseCustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { SwitchViewData } from '../../../../definitions/interfaces/sidebars/switch-view-data';
import { UserConfigsStoreFacade } from '../../../../stores/global-store/user-configs/user-configs-store.facade';
import { SidebarHeartbeatComponent } from '../../../components/sidebar-heartbeat/sidebar-heartbeat.component';
import { SwitchViewSidebarTabStore } from '../../state/switch-view-sidebar-tab-store/switch-view-sidebar-tab-store.service';
import { CountryCustomerSelectionComponent } from '../country-customer-selection/country-customer-selection.component';

@Component({
  selector: 'app-switch-online-users-tab',
  imports: [
    CwBanner,
    TranslocoDirective,
    MatRadioGroup,
    MatRadioButton,
    ReactiveFormsModule,
    CountryCustomerSelectionComponent,
    SidebarHeartbeatComponent
  ],
  providers: [
    SwitchViewSidebarTabStore
  ],
  templateUrl: './switch-online-users-tab.component.html',
  styleUrl: './switch-online-users-tab.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwitchOnlineUsersTabComponent implements OnInit {
  public isActiveTab = input.required<boolean>();
  public primaryActionClicked = input.required<Subject<void>>();
  public switchView = output<SwitchViewData>();

  public switchViewSidebarTabStore = inject(SwitchViewSidebarTabStore);
  public readonly userConfigsFacade = inject(UserConfigsStoreFacade);
  private readonly translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);

  public countryControl = new FormControl<Country | undefined>(
    this.userConfigsFacade.currentCountrySignal(),
    { validators: [ Validators.required ]}
  );

  public customerControl = new FormControl<string | BaseCustomerConfig | undefined>(
    undefined,
    { validators: [ Validators.required, this.customerValueValidator() ], nonNullable: true }
  );

  protected bannerDescription =
    this.translocoService.translate('Access to the customer view should be used only in exceptional circumstances.');

  protected reasons = [{
    labelKey: 'Written request sent by customer to create processes on their behalf.',
    value: ViewAccessReason.WRITTEN_REQUEST
  },
  {
    labelKey: 'Support request submitted by customer for issues on their side that require assistance from B. Braun employees.',
    value: ViewAccessReason.SUPPORT_REQUEST
  }];

  public reasonControl = new FormControl<ViewAccessReason>(
    this.reasons[0].value, { nonNullable: true }
  );

  public noCustomersAvailable = computed(() => !this.switchViewSidebarTabStore.allCustomersCount());

  public ngOnInit(): void {
    if (this.primaryActionClicked()) {
      this.primaryActionClicked()
        .pipe(
          filter(() => this.isActiveTab()),
          takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.switchToOnlineCustomer());
    }

    this.countryControl.valueChanges.pipe(
      startWith(this.countryControl.value),
      takeUntilDestroyed(this.destroyRef),
      filter((value): value is Country => !!value)
    ).subscribe((country: Country)=> {
      this.switchViewSidebarTabStore.loadOnlineCustomersByCountryCode(country.code);
    });

    this.customerControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((value: BaseCustomerConfig | string | undefined ) => {
      if (typeof value === 'string' || value === undefined) {
        this.switchViewSidebarTabStore.setCustomerFilter(value ?? '');
      }
    });
  }

  private customerValueValidator(): ValidatorFn {
    return (control: AbstractControl) : ValidationErrors | null => {
      const value = control.value;

      return (typeof value === 'object') ? null : { invalidCustomerValue: true };
    };
  }

  private switchToOnlineCustomer(): void {
    const country = this.countryControl.value;
    const customer = this.customerControl.value;
    const reason = this.reasonControl.value;

    if (!country) return;
    if (!customer) return;
    if (!reason) return;
    if (typeof customer === 'string') return;

    this.switchView.emit({ countryCode: country.code, customerErpRef: customer.erpRef, reason });
  }
}
